import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";

const ProfileCard = ({ profile, onSelect }) => {
  const { lang, t, pickI18n } = useLang();
  // Phase A3.1-fix: prefer the per-language `profiles.<key>` dictionary entry
  // (present for all 11 active languages in translations.js) over the backend
  // `label_{en|es|zh}` fields (which exist for EN/ES/ZH only). Backend label
  // remains the final fallback for resilience.
  const dictLabel = profile?.key ? t(`profiles.${profile.key}`) : "";
  const isDictMiss = !dictLabel || dictLabel === `profiles.${profile?.key}`;
  const label = isDictMiss
    ? (pickI18n(profile, "label") || profile.label_en)
    : dictLabel;
  // Phase A3.1-fix: localize the small category subtitle ("athlete" / "family"
  // / "medical" / …) via the new `profileCategories` dictionary. Falls back
  // to the raw backend `category` string so a future unknown category never
  // becomes a broken key.
  const dictCat = profile?.category ? t(`profileCategories.${profile.category}`) : "";
  const isCatMiss = !dictCat || dictCat === `profileCategories.${profile?.category}`;
  const category = isCatMiss ? (profile?.category || "") : dictCat;
  const bg = profile.color || "#059669";

  // Spanish-mode initials are curated per profile so they match the Spanish
  // label semantics (e.g. "Director de club o academia" → "DC", not "DD").
  // Racket Technician keeps "RT" across languages because it matches the
  // strategic profile identifier.
  const ES_INITIALS = {
    player: "J",
    racket_technician: "RT",
    parent_guardian: "PM",
    club_director: "DC",
    federation_manager: "FG",
    sports_doctor: "MD",
    physiotherapist: "F",
    sport_psychologist: "PD",
    fitness_coach: "PF",
    coach: "E",
  };

  // EN/ZH: derive from the canonical EN label so initials stay consistent
  // (and short tokens like "/" or "&" never become an initial).
  const deriveFromEn = () => {
    const src = profile.label_en || label || "";
    const tokens = src
      .split(/\s+/)
      .filter((t) => /[A-Za-z\u4E00-\u9FFF]/.test(t));
    return tokens.slice(0, 2).map((t) => t[0]).join("").toUpperCase().slice(0, 2);
  };

  const initials = lang === "es" && ES_INITIALS[profile.key]
    ? ES_INITIALS[profile.key]
    : deriveFromEn();

  return (
    <button
      type="button"
      onClick={() => onSelect?.(profile)}
      className="tsw-card tsw-card-hover text-start p-5 group flex flex-col gap-4"
      data-testid={`profile-card-${profile.key}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold text-white text-sm shadow-inner"
          style={{ backgroundColor: bg }}
          data-testid={`profile-initials-${profile.key}`}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-display font-semibold text-slate-900 text-sm leading-tight tracking-tight truncate"
            title={label}
          >
            {label}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 capitalize truncate">{category}</div>
        </div>
      </div>
      <div
        className={`text-emerald-700 group-hover:text-emerald-800 font-medium ${
          profile.key === "racket_technician"
            ? "text-xs tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
            : "text-sm"
        }`}
      >
        {t("landing.startAs")}{label} →
      </div>
    </button>
  );
};

export default ProfileCard;
